import React, { useEffect, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

interface Centre {
  Id?: string | number;
  CentreName?: string;
  CentreLat?: string | number;
  CentreLng?: string | number;
  AddressLine1?: string;
  AddressLine2?: string;
  Locality?: string;
  CityName?: string;
  Pincode?: string | number;
  StateName?: string;
}

const CentresMap = ({ centres }: { centres: Centre[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [tileError, setTileError] = useState(false);
  const locations = useMemo(() => centres.flatMap((centre) => {
    const latitude = centre?.CentreLat;
    const longitude = centre?.CentreLng;
    if (latitude == null || longitude == null ||
        String(latitude).trim() === "" || String(longitude).trim() === "") return [];
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) ||
        Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return [];
    return [{ centre, lat, lng }];
  }), [centres]);

  const showAll = () => {
    const map = mapRef.current;
    if (!map) return;
    if (locations.length) {
      map.fitBounds(L.latLngBounds(locations.map(({ lat, lng }) => [lat, lng])), {
        padding: [40, 40], maxZoom: 14,
      });
    } else {
      map.setView([22.9734, 78.6569], 5);
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false });
    mapRef.current = map;
    const tiles = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      // Production uses Referrer-Policy: same-origin. OSM requires a Referer;
      // send only our origin on cross-origin HTTPS requests, never the page path.
      referrerPolicy: "strict-origin-when-cross-origin",
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    tiles.on("tileerror", () => {
      // Stop requesting failed tiles and keep centre directions available.
      tiles.remove();
      setTileError(true);
    });
    const resize = () => map.invalidateSize();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(containerRef.current);
    window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = L.layerGroup().addTo(map);
    const icon = L.divIcon({
      className: "centres-map-marker",
      html: '<span aria-hidden="true">+</span>',
      iconSize: [34, 34], iconAnchor: [17, 34], popupAnchor: [0, -36],
    });
    locations.forEach(({ centre, lat, lng }) => {
      // Use textContent for API values so centre data cannot inject popup HTML.
      const popup = document.createElement("div");
      popup.className = "centres-map-popup";
      const title = document.createElement("h3");
      title.textContent = centre.CentreName || "Oncquest centre";
      const address = document.createElement("p");
      address.textContent = [centre.AddressLine1, centre.AddressLine2, centre.Locality,
        centre.CityName, centre.Pincode, centre.StateName].filter(Boolean).join(", ");
      const directions = document.createElement("a");
      directions.href = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      directions.target = "_blank";
      directions.rel = "noopener noreferrer";
      directions.textContent = "Get directions ↗";
      popup.append(title, address, directions);
      L.marker([lat, lng], { icon, title: title.textContent, alt: title.textContent })
        .bindPopup(popup, { maxWidth: 280 }).addTo(markers);
    });
    if (locations.length) {
      map.fitBounds(L.latLngBounds(locations.map(({ lat, lng }) => [lat, lng])), {
        padding: [40, 40], maxZoom: 14,
      });
    } else {
      map.setView([22.9734, 78.6569], 5);
    }
    return () => { markers.remove(); };
  }, [locations]);

  return (
    <section className="centres-map" aria-label="Centre locations">
      <div className="centres-map-toolbar">
        <div>
          <h2>Find a centre on the map</h2>
          <p>{locations.length} {locations.length === 1 ? "centre" : "centres"} shown · Select a pin for details</p>
        </div>
        <button type="button" onClick={showAll} disabled={!locations.length}>Show all</button>
      </div>
      <div ref={containerRef} className="centres-map-canvas" aria-label="Interactive map of Oncquest centres" />
      {!locations.length && <p className="centres-map-message" role="status">No map locations available for these results. Try another city or pincode.</p>}
      {tileError && (
        <div className="centres-map-message">
          <p role="status">The map is temporarily unavailable. Open a centre below for directions.</p>
          <ul>
            {locations.map(({ centre, lat, lng }, index) => (
              <li key={`${centre.Id || index}-${lat}-${lng}`}>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                  target="_blank" rel="noopener noreferrer">
                  {centre.CentreName || "Oncquest centre"} — Get directions
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default CentresMap;
